import {
  memo,
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { layoutHighlightStore } from "components/SmartSurveyor/SmartSurveyorV3/utils/floorPlanLayout/layoutHighlightStore";
import { LAYOUT_LABEL_STYLE_BY_VIEW, LAYOUT_STYLE_BY_VIEW } from "./constants";
import { buildLabelCandidates } from "./buildLabelCandidates";
import { placeLayoutLabels } from "./placeLayoutLabels";
import { LayoutLabel } from "./LayoutLabel";
import { LayoutPath } from "./LayoutPath";
import { LayoutTooltip } from "./LayoutTooltip";
import type { LayoutPathProps } from "./LayoutPath";
import type { FloorPlanLayoutLayerProps, HoveredLayoutShape } from "./types";

export const FloorPlanLayoutLayer = memo(function FloorPlanLayoutLayer({
  overlay,
  zoomLevel,
}: FloorPlanLayoutLayerProps) {
  const [hoveredCandidate, setHoveredCandidate] =
    useState<HoveredLayoutShape | null>(null);

  // The zones and rooms lists write the row under the cursor here; passed as a prop it would re-render the whole page.
  const highlightedShapeId = useSyncExternalStore(
    layoutHighlightStore.subscribe,
    layoutHighlightStore.get,
  );

  const handleHoverChange = useCallback<LayoutPathProps["onHoverChange"]>(
    (shape, event) =>
      setHoveredCandidate(
        shape && { shape, x: event.clientX, y: event.clientY },
      ),
    [],
  );

  // mouseleave never fires for a path replaced under the cursor (view switch, refetch), so a hover counts only while its shape is still drawn.
  const hovered = useMemo(
    () =>
      hoveredCandidate && overlay.shapes.includes(hoveredCandidate.shape)
        ? hoveredCandidate
        : null,
    [overlay.shapes, hoveredCandidate],
  );

  // The overlay's own highlight, like a merge being picked, wins over the row under the cursor.
  const shownHighlightId = overlay.highlightedShapeId ?? highlightedShapeId;

  const highlightedShape = useMemo(
    () =>
      shownHighlightId === null
        ? null
        : (overlay.shapes.find((shape) => shape.id === shownHighlightId) ??
          null),
    [overlay.shapes, shownHighlightId],
  );

  const viewStyle = LAYOUT_STYLE_BY_VIEW[overlay.view];
  const labelStyle = LAYOUT_LABEL_STYLE_BY_VIEW[overlay.view];

  // The costly part, anchor search and text measuring, runs once per layout; placement below runs per zoom step.
  const labelCandidates = useMemo(
    () =>
      buildLabelCandidates({
        shapes: overlay.shapes,
        fontWeight: labelStyle.fontWeight,
      }),
    [overlay.shapes, labelStyle.fontWeight],
  );

  // The label of the hovered or list-highlighted shape is drawn even where it would not fit or would overlap another.
  const pinnedShape = hovered?.shape ?? highlightedShape;

  const placedLabels = useMemo(
    () =>
      placeLayoutLabels({
        candidates: labelCandidates,
        zoomLevel,
        fontScreenPx: labelStyle.fontScreenPx,
        pinnedShapeId: pinnedShape?.id ?? null,
      }),
    [labelCandidates, zoomLevel, labelStyle.fontScreenPx, pinnedShape],
  );

  // The group around this layer is scaled by zoomLevel, so dividing keeps the text at fontScreenPx on screen.
  const fontSize = labelStyle.fontScreenPx / zoomLevel;

  return (
    <g
      data-testid="layout-layer"
      className={viewStyle.greyOutSiblingsClassName}
      data-highlighting={highlightedShape !== null || undefined}
    >
      {overlay.shapes.map((shape) => (
        <LayoutPath
          key={shape.id}
          shape={shape}
          view={overlay.view}
          zoneStrokeWidth={overlay.zoneStrokeWidth}
          isHighlighted={shape === highlightedShape}
          onHoverChange={handleHoverChange}
        />
      ))}
      {/* Labels after every path, so no neighbouring shape paints over them. */}
      {placedLabels.map((candidate) => (
        <LayoutLabel
          key={candidate.shapeId}
          candidate={candidate}
          fontSize={fontSize}
          fontWeight={labelStyle.fontWeight}
        />
      ))}
      {hovered?.shape.tooltip && (
        <LayoutTooltip
          tooltip={hovered.shape.tooltip}
          x={hovered.x}
          y={hovered.y}
        />
      )}
    </g>
  );
});
